import { RUMEntry, RUMFilter } from '@pulse/common/src/sdks/tracker/models/rum';
import { filter, first, from, map, merge, switchMap } from 'rxjs';
import { rumEntryMatch } from './match';
import { PerformanceTimelineService } from './performance-timeline.service';
import { RequestMonitorService } from './resource-monitor.service';

export class RUMService {
    constructor(
        private timeline: PerformanceTimelineService,
        private resource: RequestMonitorService,
    ) {}

    subscribe(report: RUMFilter[], reportOnce?: RUMFilter[]) {
        const combinedFilters = [...report, ...(reportOnce || [])];
        const resourcesEnabled = combinedFilters.some(
            (filter) =>
                filter === 'resource' ||
                (typeof filter === 'object' && filter.entryType === 'resource'),
        );

        const resources = resourcesEnabled ? this.resource.subscribe(combinedFilters) : from([]);

        const others = this.timeline
            .subscribe(combinedFilters)
            .pipe(filter((e) => e.entry_type !== 'resource'));

        const all = merge(resources, others);

        const oneTimeEntriesExcluded = all.pipe(
            filter((e) => !reportOnce || reportOnce.every((f) => !rumEntryMatch(e, f))),
        );
        const oneTimeEntries = from(reportOnce || []).pipe(
            switchMap((f) =>
                all.pipe(
                    filter((e) => rumEntryMatch(e, f)),
                    first(),
                ),
            ),
        );

        return merge(oneTimeEntries, oneTimeEntriesExcluded).pipe(
            map((entry) => this.enrichWithServerAttributes(entry)),
        );
    }

    private enrichWithServerAttributes(entry: RUMEntry) {
        if (Array.isArray(entry.server_timing)) {
            const entries = [...entry.server_timing];
            const attributes: Record<string, string> = {};

            for (const timingEntry of entry.server_timing) {
                const matches = /^pulse\$([^$]+)\$([^$]+)$/.exec(timingEntry.name);

                if (!matches?.length) {
                    continue;
                }

                const key = (matches[1] || '').toLowerCase();
                const value = (matches[2] || '').toLowerCase();

                attributes[key] = value;

                entries.splice(entries.indexOf(timingEntry), 1);
            }

            entry.server_timing = entries;
            entry.server_attributes = attributes;
        }

        return entry;
    }
}
